import { VersionStatusBadge } from './VersionStatusBadge';
import { ChangelogText } from './ChangelogText';
import { useNavigate } from '@tanstack/react-router';
import { T, useGT } from 'gt-react';
import { Button } from 'src/components/ui/button';
import { Badge } from 'src/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'src/components/ui/dropdown-menu';
import {
  CaretDownIcon,
  SealCheckIcon,
  CircleNotchIcon,
} from '@phosphor-icons/react';
import { cn } from 'src/lib/utils';
import { useListWorkflowVersions } from 'src/api/rpc/hooks/workflows/useWorkflowVersioning';

/** Radix menu items default to focus-only highlight; this makes rows feel obviously clickable. */
const menuRowInteractive =
  'cursor-pointer transition-colors duration-150 hover:bg-accent hover:text-accent-foreground data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground';

interface VersionPickerDropdownProps {
  /** The Workflow (lineage) id — used to fetch versions and build nav URLs. */
  workflowId: string;
  currentVersionId: string;
  currentVersionNumber: number;
  currentStatus: 'draft' | 'finalized';
  currentIsPinned: boolean;
  /** Opens the full version history dialog (Pin / changelog / etc). Omit on
   *  surfaces that don't own a dialog — the dropdown hides the menu item
   *  and users get to the full history via the "View all versions" link. */
  onOpenFullHistory?: () => void;
  /**
   * When true, switching versions preserves edit mode (adds `?edit=true`) so
   * the user stays in the editor. When false (default), navigates without
   * the edit param — the layout route picks whichever tab the user is on.
   * Set true when rendering inside the editor header; leave false when the
   * picker sits on the workflow detail layout (next to Duplicate / Run Now).
   */
  stayInEditor?: boolean;
  /**
   * When true, other versions in the list are not navigable (e.g. while
   * editing a draft — switching would fight `?edit=true` and the loader).
   * Footer actions (version history, view all) stay available.
   */
  versionSwitchDisabled?: boolean;
}

/**
 * Quick-switch dropdown next to the version label in the editor header.
 * Lists versions; clicking one navigates. "View all versions" jumps to the
 * Versions tab and "Open version history" pops the pin/changelog dialog.
 */
export function VersionPickerDropdown({
  workflowId,
  currentVersionId,
  currentVersionNumber,
  currentStatus,
  currentIsPinned,
  onOpenFullHistory,
  stayInEditor = false,
  versionSwitchDisabled = false,
}: VersionPickerDropdownProps) {
  const gt = useGT();
  const navigate = useNavigate();
  const { data, isLoading } = useListWorkflowVersions(workflowId);

  const versions = data?.versions ?? [];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1.5"
          title={
            versionSwitchDisabled
              ? gt(
                  'Version is fixed while editing this draft — use Save & Publish or cancel to switch',
                  {
                    context:
                      'tooltip when version switching is disabled during draft editing',
                  },
                )
              : gt('Switch version', {
                  context: 'tooltip for the version picker dropdown button',
                })
          }
        >
          <span className="font-mono">
            <T context="version number prefix label, followed by the version number">
              v
            </T>
            {currentVersionNumber}
          </span>
          {currentStatus === 'draft' ? (
            <Badge
              variant="secondary"
              className="h-4 px-1 text-[10px] font-normal"
            >
              <T context="badge label for a draft workflow version">Draft</T>
            </Badge>
          ) : currentIsPinned ? (
            <Badge className="h-4 px-1 text-[10px] font-normal">
              <SealCheckIcon className="mr-0.5 h-2.5 w-2.5" weight="fill" />
              <T context="badge label for the published (pinned) workflow version">
                Published
              </T>
            </Badge>
          ) : (
            <Badge
              variant="outline"
              className="h-4 px-1 text-[10px] font-normal border-muted-foreground/25 text-muted-foreground"
              title={gt(
                'This version is finalized but not published — schedules and triggers use the published version',
                {
                  context:
                    'tooltip for an unpublished finalized workflow version badge',
                },
              )}
            >
              <T context="badge label for a finalized but unpublished workflow version">
                Unpublished
              </T>
            </Badge>
          )}
          <CaretDownIcon className="h-3 w-3 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="min-w-[360px] max-w-md max-h-[70vh] overflow-y-auto"
      >
        <DropdownMenuLabel>
          <T context="dropdown menu label for selecting a workflow version">
            Jump to version
          </T>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <CircleNotchIcon className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
          </div>
        ) : versions.length === 0 ? (
          <div className="py-2 px-2 text-xs text-muted-foreground">
            <T context="empty state when no workflow versions exist">
              No versions.
            </T>
          </div>
        ) : (
          versions.map((v) => {
            const isCurrent = v.id === currentVersionId;
            const rowDisabled = isCurrent || versionSwitchDisabled;
            return (
              <div key={v.id}>
                <DropdownMenuItem
                  disabled={rowDisabled}
                  aria-current={isCurrent ? 'true' : undefined}
                  onClick={() => {
                    // The pinned version is the canonical URL — route to the
                    // bare workflow path. Any other version gets the explicit
                    // `/v/$versionNumber` segment.
                    if (v.isPinned) {
                      navigate({
                        to: '/workflows/$workflowId',
                        params: { workflowId },
                        search: stayInEditor ? { edit: true } : undefined,
                      });
                    } else {
                      navigate({
                        to: '/workflows/$workflowId/v/$versionNumber',
                        params: {
                          workflowId,
                          versionNumber: String(v.version),
                        },
                        search: stayInEditor ? { edit: true } : undefined,
                      });
                    }
                  }}
                  className={cn(
                    'flex items-center justify-between gap-2',
                    rowDisabled
                      ? 'cursor-default bg-muted/50 text-muted-foreground data-[disabled]:opacity-100'
                      : menuRowInteractive,
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 min-w-0 flex-1">
                      <span className="font-mono text-xs font-medium">
                        <T context="version number prefix label, followed by the version number">
                          v
                        </T>
                        {v.version}
                      </span>
                      {/* Pick the most informative single badge — Published
                      supersedes Finalized; Finalized only applies to
                      finalized-but-not-published rows. */}
                      <VersionStatusBadge
                        status={v.status}
                        isPinned={v.isPinned}
                        compact
                      />
                      {v.createdByName && (
                        <span className="ml-auto text-xs text-muted-foreground truncate">
                          {v.createdByName}
                        </span>
                      )}
                    </div>
                  </div>
                  {isCurrent ? (
                    <span className="shrink-0 text-[10px] uppercase tracking-wide text-muted-foreground">
                      <T context="label indicating this is the currently viewed workflow version">
                        Viewing
                      </T>
                    </span>
                  ) : null}
                </DropdownMenuItem>
                {v.changelog && (
                  <div className="px-2 pb-2">
                    <ChangelogText changelog={v.changelog} />
                  </div>
                )}
              </div>
            );
          })
        )}
        <DropdownMenuSeparator />
        {onOpenFullHistory && (
          <DropdownMenuItem
            onClick={onOpenFullHistory}
            className={menuRowInteractive}
          >
            <T context="dropdown menu item to open the full workflow version history dialog">
              Open version history…
            </T>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={() => {
            // Navigate to the Versions tab on the pinned workflow URL.
            navigate({
              to: '/workflows/$workflowId/versions',
              params: { workflowId },
            });
          }}
          className={menuRowInteractive}
        >
          <T context="dropdown menu item link to view all workflow versions on the versions tab">
            View all versions →
          </T>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
